'use client'

import { Activity, CreditCard, FolderOpen, ArrowRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DashboardData,
  DashboardTab,
  DocModule,
  DOC_MODULE_ORDER,
  MODULE_META,
  formatCredits,
} from '../types'

interface Props {
  data: DashboardData
  onGoToTab: (tab: DashboardTab) => void
  onRecharge: () => void
}

export default function OverviewSection({ data, onGoToTab, onRecharge }: Props) {
  const { profile, activity, credits_added, documents } = data
  const totalOps =
    activity.transcription + activity.codification + activity.note + activity.correction + activity.rapport

  return (
    <div className="space-y-5">
      {/* Solde */}
      <div className="rounded-xl bg-tq-green p-5 text-white">
        <p className="text-sm opacity-80 mb-1">Solde disponible</p>
        <p className="text-3xl font-bold">
          {profile.is_admin ? '∞' : formatCredits(profile.credits)}
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs capitalize">
            Plan {profile.plan}
          </Badge>
          {profile.email && <span className="text-xs opacity-70">{profile.email}</span>}
        </div>
        {!profile.is_admin && (
          <Button
            size="sm"
            onClick={onRecharge}
            className="mt-4 bg-white text-tq-green hover:bg-white/90 gap-1.5"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Recharger
          </Button>
        )}
      </div>

      {/* Compteurs clés */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-tq-border bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-4 w-4 text-tq-green" />
            <span className="text-xs text-tq-muted font-medium">Opérations totales</span>
          </div>
          <p className="text-xl font-bold text-tq-text">{totalOps.toLocaleString('fr-FR')}</p>
        </div>
        <div className="rounded-lg border border-tq-border bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-orange-500" />
            <span className="text-xs text-tq-muted font-medium">Crédits rechargés</span>
          </div>
          <p className="text-xl font-bold text-tq-text">{formatCredits(credits_added)}</p>
        </div>
      </div>

      {/* Aperçu activité par module */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-tq-text uppercase tracking-wider">Activité par module</p>
          <button
            onClick={() => onGoToTab('stats')}
            className="text-xs text-tq-green font-medium hover:underline flex items-center gap-1"
          >
            Statistiques <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="space-y-2">
          {DOC_MODULE_ORDER.map((mod: DocModule) => {
            const meta = MODULE_META[mod]
            const count = activity[meta.activityKey]
            const docCount = documents[mod]?.length ?? 0
            return (
              <button
                key={mod}
                onClick={() => onGoToTab('documents')}
                className="w-full flex items-center justify-between rounded-lg border border-tq-border bg-white px-4 py-3 hover:border-tq-green/50 transition-colors text-left"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-base">{meta.emoji}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-tq-text truncate">{meta.label}</p>
                    <p className="text-xs text-tq-muted">
                      {docCount} document{docCount > 1 ? 's' : ''} récent{docCount > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-bold text-tq-green shrink-0 ml-2">{count}</span>
              </button>
            )
          })}
        </div>
        <button
          onClick={() => onGoToTab('documents')}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-tq-green font-medium hover:underline"
        >
          <FolderOpen className="h-3.5 w-3.5" /> Voir tous les documents
        </button>
      </div>
    </div>
  )
}
